/**
 * 下注合法性、最小加注与行动权推进。见 docs/ARCHITECTURE.md §4.6 / §4.7 / §4.10。
 *
 * 本文件是全项目最容易写错的地方，三条规则必须逐字成立：
 *
 *  - 红线 #18：`to` 恒为「本轮累计投入总额」（raise-to），绝不是增量。
 *  - 红线 #21：`canRaise` 是能否加注的唯一判据，UI 绝不用 `minRaiseTo < maxRaiseTo` 推断。
 *    短码只能全下时两者相等但仍可加注；不完整全下不重开加注权时两者不等却不可加注——
 *    两个方向都会判错。
 *  - 红线 #22：行动权推进一律走 `nextToAct`，`firstToActPreflop/Postflop` 只能做 UI 提示。
 *
 * 铁律 #1：纯函数。本文件不读时钟、不碰 RNG、不改入参的任何子结构。
 */
import type { Chips, SeatIndex } from '../ids.js'
import { engineErr, engineOk, isBettingPhase } from './types.js'
import type {
  ActionIntent,
  ActionRequest,
  AppliedAction,
  AppliedActionType,
  EngineErrorCode,
  EngineResult,
  HandPlayer,
  HandState,
  LegalActions,
  PlayerHandStatus,
  Street,
} from './types.js'
import { sizingPresets } from './sizing.js'

// ============================================================================
// 内部小工具
// ============================================================================

function seatsOf(state: HandState): readonly HandPlayer[] {
  return Object.values(state.players)
}

/**
 * 底池口径（§4.10）：「已收池 + 本街所有人已投入」= Σ committedThisHand（退还后）。
 *
 * 不用 `Σ pots.amount + Σ committedThisRound`：`pots` 只在每条街结束时重建，而 ante
 * 计入 committedThisHand 却**不**计入 committedThisRound，于是翻前（pots 尚未建立）
 * 那个公式会把所有 ante 漏掉，pot-sized bet 直接算小一截。
 */
export function computePotForSizing(state: HandState): Chips {
  let sum = 0
  for (const p of seatsOf(state)) sum += p.committedThisHand
  return sum
}

/** 座位位图。maxSeats <= 9，稳稳落在 int32 内。 */
function statusMask(state: HandState, status: PlayerHandStatus): number {
  let mask = 0
  for (const p of seatsOf(state)) {
    if (p.status === status) mask |= 1 << p.seat
  }
  return mask
}

/**
 * 本街此前的下注层级计数（§12.3）：翻前大盲注本身算第 1 个下注，
 * 于是开池 = 2-bet（betsBefore=1）、再加注 = 3-bet（betsBefore=2）；翻后首次下注为 0。
 * `call` 永不增加层级；**短全下（reopenedAction=false 的 all-in raise）仍然增加一级**，
 * 与主流追踪软件一致。
 */
function betsBeforeOnStreet(state: HandState, street: Street): number {
  let n = street === 'preflop' ? 1 : 0
  for (const a of state.actionLog) {
    if (a.street === street && (a.type === 'bet' || a.type === 'raise')) n += 1
  }
  return n
}

function blockedActions(
  seat: SeatIndex,
  reason: EngineErrorCode,
  p: HandPlayer | undefined,
  potForSizing: Chips,
): LegalActions {
  const committed = p === undefined ? 0 : p.committedThisRound
  return {
    seat,
    onAction: false,
    canFold: false,
    canCheck: false,
    canCall: false,
    toCall: 0,
    callTo: committed,
    callIsAllIn: false,
    canRaise: false,
    raiseBlockedReason: null,
    raiseKind: null,
    minRaiseTo: 0,
    minRaiseToFull: 0,
    maxRaiseTo: p === undefined ? 0 : committed + p.stack,
    onlyAllIn: false,
    potForSizing,
    sizingPresets: [],
    disabledReason: reason,
  }
}

// ============================================================================
// legalActions（§4.6）
// ============================================================================

export function legalActions(state: HandState, seat: SeatIndex): LegalActions {
  const potForSizing = computePotForSizing(state)

  const p = state.players[seat]
  if (p === undefined) return blockedActions(seat, 'UNKNOWN_SEAT', undefined, potForSizing)
  if (!isBettingPhase(state.phase)) {
    return blockedActions(seat, 'HAND_NOT_IN_BETTING', p, potForSizing)
  }
  if (state.actionOn !== seat) return blockedActions(seat, 'NOT_YOUR_TURN', p, potForSizing)
  if (p.status !== 'active') return blockedActions(seat, 'CANNOT_ACT', p, potForSizing)

  const toCall = Math.max(0, state.betToMatch - p.committedThisRound)
  const maxTo = p.committedThisRound + p.stack
  const canCheck = toCall === 0
  const canCall = toCall > 0 && p.stack > 0
  const callTo = Math.min(state.betToMatch, maxTo)
  const callIsAllIn = toCall >= p.stack

  // 除本人外，还有能跟注的对手吗——全员已全下时加注毫无意义（钱只会被原路退还）
  let opponentCanCall = false
  for (const o of seatsOf(state)) {
    if (o.seat !== seat && o.status === 'active' && o.stack > 0) {
      opponentCanCall = true
      break
    }
  }

  const hasRoom = maxTo > state.betToMatch
  // ★ 不完整全下**不**重置本人的 hasActedThisRound，这正是「不重开加注权」的载体：
  //   已行动过的人只能 call/fold，尚未行动过的人（hasActed=false）仍可加注。
  const actionOpen = p.hasActedThisRound === false
  const canRaise = hasRoom && actionOpen && opponentCanCall
  const raiseBlockedReason = !hasRoom
    ? ('noChips' as const)
    : !actionOpen
      ? ('notReopened' as const)
      : !opponentCanCall
        ? ('noOpponentCanCall' as const)
        : null

  const raiseKind = canRaise ? (state.betToMatch === 0 ? 'bet' : 'raise') : null
  // minRaiseToFull 即便不可加注也给出未钳位值，UI 用它解释「差多少才够加注」
  const minRaiseToFull =
    state.betToMatch === 0
      ? state.config.bigBlind
      : state.betToMatch + state.lastFullRaiseSize
  const minRaiseTo = canRaise ? Math.min(minRaiseToFull, maxTo) : 0
  const onlyAllIn = canRaise && minRaiseToFull > maxTo

  const presets = canRaise
    ? sizingPresets({
        committedThisRound: p.committedThisRound,
        toCall,
        potForSizing,
        minRaiseTo,
        maxRaiseTo: maxTo,
        bigBlind: state.config.bigBlind,
      })
    : []

  return {
    seat,
    onAction: true,
    // 恒为 true：断线/超时需要统一的 fold 通道，误触由 UI 层拦截（§11.5），引擎不做产品决策
    canFold: true,
    canCheck,
    canCall,
    toCall,
    callTo,
    callIsAllIn,
    canRaise,
    raiseBlockedReason,
    raiseKind,
    minRaiseTo,
    minRaiseToFull,
    maxRaiseTo: maxTo,
    onlyAllIn,
    potForSizing,
    sizingPresets: presets,
  }
}

// ============================================================================
// validateAction（§4.6）
// ============================================================================

/** 加注被拒的原因 → 错误码。`noChips` 只在「有钱但不够超过当前注」时出现。 */
function raiseBlockedCode(la: LegalActions): EngineErrorCode {
  switch (la.raiseBlockedReason) {
    case 'notReopened':
      return 'ACTION_NOT_REOPENED'
    case 'noOpponentCanCall':
      return 'NO_OPPONENT_CAN_CALL'
    default:
      return la.raiseKind === 'bet' || la.toCall === 0 ? 'BET_NOT_ALLOWED' : 'RAISE_NOT_ALLOWED'
  }
}

/** 归一化之后的意图：`allIn` 已被换算成 call / bet / raise，不再出现。 */
type NormalizedIntent = Exclude<ActionIntent, { readonly type: 'allIn' }>

/**
 * `allIn` 归一化为 call / bet / raise（§4.6）。
 *
 * 全部筹码都不够跟注 → 这是一次全下跟注；有余量且可加注 → 全下加注。
 * 有余量但不可加注（例如不完整全下后已行动过的人）时**报错而不是静默降级为 call**：
 * 玩家点的是「全下」，把它悄悄变成少投一大截的 call 是引擎单方面改写玩家意图。
 */
function normalizeAllIn(state: HandState, la: LegalActions): EngineResult<NormalizedIntent> {
  if (la.maxRaiseTo <= state.betToMatch) return engineOk({ type: 'call' })
  if (la.canRaise) {
    return engineOk({
      type: la.raiseKind === 'bet' ? 'bet' : 'raise',
      to: la.maxRaiseTo,
    })
  }
  return engineErr(raiseBlockedCode(la), '当前不可加注，全下无法归一化', la.seat)
}

/**
 * 幂等重放比对（§4.6 步骤 4）。
 *
 * 只能拿「已落库的 AppliedAction」比，不能重新跑归一化——当时的 state 已经不在了。
 * `bet`/`raise` 不区分：两者的差别只是 betToMatch 是否为 0，是同一个 raise-to 动作的两个称呼。
 * `allIn` 命中任何 `allIn === true` 的条目即算一致（结果状态完全相同）。
 */
function matchesLogged(intent: ActionIntent, a: AppliedAction): boolean {
  switch (intent.type) {
    case 'fold':
      return a.type === 'fold'
    case 'check':
      return a.type === 'check'
    case 'call':
      return a.type === 'call'
    case 'bet':
    case 'raise':
      return (a.type === 'bet' || a.type === 'raise') && a.to === intent.to
    case 'allIn':
      // 必须排除 postBigBlind / postSmallBlind / postAnte / postDead 这些强制投入条目：
      // 它们也可能 allIn === true（筹码不足以交满盲注）。若放行，一个 seq 指向盲注条目的
      // allIn 请求会被当成「幂等重放成功」返回 ok 且状态不变，客户端以为动作已生效不再重发，
      // 实际本手根本没动作，最后超时被自动弃牌。§4.6 步骤 4 要求这种情况返回 STALE_ACTION。
      return (a.type === 'call' || a.type === 'bet' || a.type === 'raise') && a.allIn
  }
}

interface AppliedDraft {
  readonly type: AppliedActionType
  readonly to: Chips
  readonly delta: Chips
  readonly allIn: boolean
  readonly reopenedAction: boolean
}

function buildApplied(
  state: HandState,
  p: HandPlayer,
  la: LegalActions,
  req: ActionRequest,
  street: Street,
  d: AppliedDraft,
): AppliedAction {
  return {
    index: state.actionLog.length,
    street,
    seat: req.seat,
    type: d.type,
    to: d.to,
    delta: d.delta,
    allIn: d.allIn,
    reopenedAction: d.reopenedAction,
    source: req.source,
    potBefore: la.potForSizing,
    stackBefore: p.stack,
    streetInvestedBefore: p.committedThisRound,
    toCallBefore: la.toCall,
    minRaiseToBefore: la.canRaise ? la.minRaiseTo : null,
    maxRaiseToBefore: la.maxRaiseTo,
    betsBefore: betsBeforeOnStreet(state, street),
    activeMask: statusMask(state, 'active'),
    allInMask: statusMask(state, 'allIn'),
  }
}

/**
 * 校验顺序不可调换（§4.6、红线 #36）。
 *
 * 步骤 1 必须先于 3/4：上一手的陈旧包延迟落到下一手时 `(seat,type,to)` 极易碰撞
 * （`call 大盲`、`check` 都是高频动作），没有 handId 校验就会被误判成「幂等重放成功」，
 * 客户端以为动作已生效不再重发，实际本手根本没动作，直接超时被自动弃牌。
 */
export function validateAction(
  state: HandState,
  req: ActionRequest,
): EngineResult<AppliedAction> {
  // 1
  if (req.handId !== state.config.handId) {
    return engineErr('WRONG_HAND', '动作属于另一手牌', req.seat)
  }
  // 2
  const phase = state.phase
  if (!isBettingPhase(phase)) {
    return engineErr('HAND_NOT_IN_BETTING', '当前阶段不接受下注动作', req.seat)
  }
  // 3
  if (req.seq > state.actionLog.length) {
    return engineErr('FUTURE_ACTION', '动作序号超前', req.seat)
  }
  // 4：幂等重放
  if (req.seq < state.actionLog.length) {
    const logged = state.actionLog[req.seq]
    if (logged !== undefined && logged.seat === req.seat && matchesLogged(req.intent, logged)) {
      return engineOk(logged)
    }
    return engineErr('STALE_ACTION', '动作序号已过期', req.seat)
  }
  // 5
  if (state.actionOn !== req.seat) {
    return engineErr('NOT_YOUR_TURN', '尚未轮到该座位行动', req.seat)
  }
  // 6
  const p = state.players[req.seat]
  if (p === undefined) return engineErr('UNKNOWN_SEAT', '座位不在本手牌中', req.seat)
  if (p.status !== 'active') return engineErr('CANNOT_ACT', '该座位已弃牌或全下', req.seat)

  // 7：金额与动作合法性
  const la = legalActions(state, req.seat)
  const street: Street = state.street ?? phase

  let intent: NormalizedIntent
  if (req.intent.type === 'allIn') {
    const normalized = normalizeAllIn(state, la)
    if (!normalized.ok) return normalized
    intent = normalized.value
  } else {
    intent = req.intent
  }

  switch (intent.type) {
    case 'fold':
      return engineOk(
        buildApplied(state, p, la, req, street, {
          type: 'fold',
          to: p.committedThisRound,
          delta: 0,
          allIn: false,
          reopenedAction: false,
        }),
      )

    case 'check':
      if (!la.canCheck) {
        return engineErr('CHECK_NOT_ALLOWED', '面对下注不能过牌', req.seat)
      }
      return engineOk(
        buildApplied(state, p, la, req, street, {
          type: 'check',
          to: p.committedThisRound,
          delta: 0,
          allIn: false,
          reopenedAction: false,
        }),
      )

    case 'call': {
      if (la.toCall === 0) {
        return engineErr('NOTHING_TO_CALL', '无需跟注', req.seat)
      }
      // 跟注不足自动全下：delta = min(toCall, stack)，且**不改变 betToMatch**（边池的来源）
      const delta = Math.min(la.toCall, p.stack)
      return engineOk(
        buildApplied(state, p, la, req, street, {
          type: 'call',
          to: p.committedThisRound + delta,
          delta,
          allIn: delta >= p.stack,
          reopenedAction: false,
        }),
      )
    }

    case 'bet':
    case 'raise': {
      if (!la.canRaise) {
        return engineErr(raiseBlockedCode(la), '当前不可加注', req.seat)
      }
      const to = intent.to
      // isLegalTo(to)：整数 ∧ to <= maxTo ∧ to > betToMatch ∧ (to >= minRaiseToFull ∨ to === maxTo)
      if (!Number.isInteger(to)) {
        return engineErr('AMOUNT_NOT_INTEGER', '筹码必须是整数', req.seat)
      }
      if (to > la.maxRaiseTo) {
        return engineErr('AMOUNT_ABOVE_STACK', '超过可用筹码', req.seat)
      }
      if (to <= state.betToMatch) {
        return engineErr('AMOUNT_NOT_ABOVE_CURRENT_BET', '加注额必须高于当前注', req.seat)
      }
      // 第二项即「不完整全下」的特许：筹码不够最小加注时允许把剩余筹码全推
      if (to < la.minRaiseToFull && to !== la.maxRaiseTo) {
        return engineErr('AMOUNT_BELOW_MIN', '低于最小加注额', req.seat)
      }
      return engineOk(
        buildApplied(state, p, la, req, street, {
          type: la.raiseKind === 'bet' ? 'bet' : 'raise',
          to,
          delta: to - p.committedThisRound,
          allIn: to === la.maxRaiseTo,
          // 只有「完整」加注才重开加注权；不完整全下 reopenedAction=false
          reopenedAction: to >= la.minRaiseToFull,
        }),
      )
    }
  }
}

// ============================================================================
// 下注轮结束与行动权推进（§4.7）
// ============================================================================

export function isBettingRoundComplete(state: HandState): boolean {
  const contenders = seatsOf(state).filter((p) => p.status === 'active')
  if (contenders.length === 0) return true
  if (contenders.length === 1) {
    // 不检查 hasActed：其余人全部全下、唯一有筹码者已补平时没有任何可下注的对手，
    // 本轮立即结束（不给他「下注给空气」的机会）
    const only = contenders[0] as HandPlayer
    return only.committedThisRound >= state.betToMatch
  }
  return contenders.every(
    (p) => p.hasActedThisRound && p.committedThisRound === state.betToMatch,
  )
}

/**
 * 从 `fromSeat`（含）起顺时针找第一个 `status === 'active'` 的座位。
 *
 * 红线 #22：这是行动权推进的唯一入口。预计算的 `firstToActPreflop/Postflop` 只能做 UI 提示——
 * 那个座位很可能已弃牌或被 ante 打成全下，直接用会把整手牌卡死。
 * 单挑无需特判：翻前 `bigBlindSeat + 1` 顺时针第一个 active 恰是按钮/小盲，
 * 翻后 `buttonSeat + 1` 恰是大盲。
 */
export function nextToAct(state: HandState, fromSeat: SeatIndex): SeatIndex | null {
  const maxSeats = state.config.maxSeats
  if (maxSeats <= 0) return null
  // fromSeat 常以 `startSeat + 1` 传入，可能恰好等于 maxSeats；负数同样归一
  const start = ((Math.trunc(fromSeat) % maxSeats) + maxSeats) % maxSeats
  for (let i = 0; i < maxSeats; i += 1) {
    const seat = (start + i) % maxSeats
    const p = state.players[seat]
    if (p !== undefined && p.status === 'active') return seat
  }
  return null
}

/**
 * 超时/断线动作的唯一产出口（红线 #26）。
 *
 * 引擎不含时钟，但超时动作的**内容**必须是状态的纯函数，否则 `replayHand` 不再确定。
 * 服务端超时/断线时必须调用本函数，不得自行决定；`source` 填 'timeout' / 'disconnect'，
 * 且 `source` 不参与任何状态计算。
 */
export function defaultAutoIntent(state: HandState, seat: SeatIndex): ActionIntent {
  return legalActions(state, seat).canCheck ? { type: 'check' } : { type: 'fold' }
}
